import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, Loading, ErrorState, SectionCard, fmtINR, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const weekOf = (s, wk) => s.weeks.find((w) => w.week === wk) || { orders: 0, offline_sales: 0, commission: 0, outstanding: 0, status: "NO_ORDERS" };

function PendingPayments({ shop, onReview }) {
  return shop.payments.filter((p) => p.status === "PENDING_VERIFICATION").map((p) => (
    <div key={p.id} className="text-xs flex flex-wrap items-center gap-1">
      <span>{fmtINR(p.amount)} · ref <span className="font-mono">{p.reference}</span> · {fmtDate(p.submitted_at)}</span>
      <Button size="sm" className="h-6 px-2" onClick={() => onReview(p, "verify")} data-testid={`settlement-verify-${p.id}`}>Confirm received</Button>
      <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => onReview(p, "reject")} data-testid={`settlement-reject-${p.id}`}>Reject</Button>
    </div>));
}

function ShopRow({ s, week, onReview }) {
  const w = weekOf(s, week);
  return (
    <tr className="border-t align-top" data-testid={`settlement-row-${s.shop_id}`}>
      <td className="py-2">{s.shop_name}<div className="text-xs text-muted-foreground">{s.commission_percent}% {s.commission_source === "custom" ? "(custom)" : "(default)"}</div></td>
      <td className="tabular-nums" data-testid={`settlement-week-orders-${s.shop_id}`}>{w.orders}</td>
      <td className="tabular-nums">{fmtINR(w.offline_sales)}</td>
      <td className="tabular-nums">{fmtINR(w.commission)}<div className="text-xs text-muted-foreground">{w.status.replace("_", " ").toLowerCase()}</div></td>
      <td className={`tabular-nums ${s.previous_dues > 0 ? "text-red-600" : ""}`}>{fmtINR(s.previous_dues)}</td>
      <td className="tabular-nums text-xs">reported {fmtINR(s.payments_reported)}<div>verified {fmtINR(s.verified_paid)}</div><div>pending {fmtINR(s.pending_verification)}</div></td>
      <td className="tabular-nums font-medium" data-testid={`settlement-outstanding-${s.shop_id}`}>{fmtINR(s.total_due)}</td>
      <td className="space-y-1"><PendingPayments shop={s} onReview={onReview} /></td>
    </tr>
  );
}

export function SettlementReview() {
  const { data, loading, error, refetch } = useFetch("/admin/settlements", []);
  const weeks = useMemo(() => {
    const set = new Set(data?.shops.flatMap((s) => [s.current_week.week, ...s.weeks.map((w) => w.week)]) || []);
    return [...set].sort().reverse();
  }, [data]);
  const [week, setWeek] = useState(null);
  const wk = week || weeks[0];
  const review = async (p, action) => {
    try { await api.post(`/admin/settlement-payments/${p.id}/review`, { action }); toast.success(action === "verify" ? "Payment confirmed" : "Payment rejected"); refetch(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-5" data-testid="settlement-review">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-semibold font-outfit">Weekly COD commission by shop</h3>
        {weeks.length > 0 && (
          <Select value={wk} onValueChange={setWeek}>
            <SelectTrigger className="w-40 h-8" data-testid="settlement-week-select"><SelectValue /></SelectTrigger>
            <SelectContent>{weeks.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>)}
        {data && <span className="ml-auto text-sm" data-testid="settlement-total-due">Total outstanding {fmtINR(data.total_due)}</span>}
      </div>
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : !data.shops.length ? <p className="text-sm text-muted-foreground" data-testid="settlement-empty">No cash-order commission yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[980px] text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2">Shop · rate</th><th>Orders</th><th>Sales</th><th>Commission</th><th>Earlier dues</th><th>Payments</th><th>Outstanding</th><th>To confirm</th></tr></thead>
          <tbody>{data.shops.map((s) => <ShopRow key={s.shop_id} s={s} week={wk} onReview={review} />)}</tbody>
        </table></div>)}
      <p className="text-xs text-muted-foreground mt-2">Weeks run Monday to Sunday (India time). Confirmed payments clear the oldest week first.</p>
    </SectionCard>
  );
}
